import { useState, useRef, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface KeywordTarget {
  id: string
  keyword: string
  location: string
  language: string
  device: string
  searchEngine: string
  createdAt: string
  latestRank: number | null
  latestUrl: string | null
  latestCheckedAt: string | null
  bestRank: number | null
  searchVolume: number | null
  cpc: number | null
  competition: number | null
  difficulty: number | null
  intent: string | null
  enrichedAt: string | null
  previousRank: number | null
  serpFeatures: string[] | null
  aiOverviewPresent: boolean
  aiOverviewExists: boolean
}

export interface KeywordRankHistory {
  checkedAt: string
  position: number | null
  url: string | null
}

/** List tracked keywords with latest rank */
export function useKeywords(siteId: string) {
  return useQuery({
    queryKey: ['keywords', siteId],
    queryFn: () => apiClient.get<{
      keywords: KeywordTarget[]
      configured: boolean
    }>(`/api/sites/${siteId}/keywords`),
    enabled: !!siteId,
    staleTime: 2 * 60 * 1000,
  })
}

/** Add keywords to track */
export function useAddKeywords(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { keywords: string[]; location?: string; language?: string; searchEngine?: string }) =>
      apiClient.post<{ added: number }>(`/api/sites/${siteId}/keywords`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keywords', siteId] })
    },
  })
}

/** Remove a keyword */
export function useRemoveKeyword(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (keywordId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/sites/${siteId}/keywords/${keywordId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keywords', siteId] })
    },
  })
}

/** Get rank history for a keyword */
export function useKeywordHistory(siteId: string, keywordId: string, days = 30) {
  return useQuery({
    queryKey: ['keywords', 'history', siteId, keywordId, days],
    queryFn: () => apiClient.get<{
      keyword: string
      history: KeywordRankHistory[]
    }>(`/api/sites/${siteId}/keywords/${keywordId}/history`, {
      params: { days },
    }),
    enabled: !!siteId && !!keywordId,
    staleTime: 5 * 60 * 1000,
  })
}

// ── Check keywords with polling ─────────────────────────────────

export type CheckState = 'idle' | 'posted' | 'collecting' | 'done'

export interface CheckProgress {
  state: CheckState
  collected: number
  total: number
  message: string
}

const POLL_INTERVAL = 15_000 // 15 seconds
const MAX_POLLS = 12 // 3 minutes total
const INITIAL_DELAY = 15_000 // Wait 15s before first collect attempt
const DONE_DISPLAY_MS = 5_000 // Show "done" for 5 seconds

/** Trigger manual rank check with polling-based result collection */
export function useCheckKeywords(siteId: string) {
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<CheckProgress>({
    state: 'idle', collected: 0, total: 0, message: '',
  })

  const pollCountRef = useRef(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const totalPostedRef = useRef(0)
  const totalCollectedRef = useRef(0)

  const stopPolling = useCallback(() => {
    if (delayRef.current) {
      clearTimeout(delayRef.current)
      delayRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Clean up on unmount
  useEffect(() => stopPolling, [stopPolling])

  const collectResults = useCallback(async () => {
    try {
      const result = await apiClient.post<{ collected: number; pending: number }>(
        `/api/sites/${siteId}/keywords/collect`
      )
      totalCollectedRef.current += result.collected

      if (result.collected > 0) {
        queryClient.invalidateQueries({ queryKey: ['keywords', siteId] })
      }

      pollCountRef.current++

      if (result.pending === 0 || pollCountRef.current >= MAX_POLLS) {
        // All done or timed out
        stopPolling()
        setProgress({
          state: 'done',
          collected: totalCollectedRef.current,
          total: totalPostedRef.current,
          message: totalCollectedRef.current > 0
            ? `Rankings updated (${totalCollectedRef.current} results)`
            : 'No new results found',
        })
        // Reset to idle after display period
        setTimeout(() => {
          setProgress({ state: 'idle', collected: 0, total: 0, message: '' })
        }, DONE_DISPLAY_MS)
      } else {
        setProgress({
          state: 'collecting',
          collected: totalCollectedRef.current,
          total: totalPostedRef.current,
          message: `Collecting results... (${totalCollectedRef.current} of ${totalPostedRef.current})`,
        })
      }
    } catch {
      // On error, keep polling (transient failures happen)
      pollCountRef.current++
      if (pollCountRef.current >= MAX_POLLS) {
        stopPolling()
        setProgress({
          state: 'done',
          collected: totalCollectedRef.current,
          total: totalPostedRef.current,
          message: totalCollectedRef.current > 0
            ? `Partial results (${totalCollectedRef.current} of ${totalPostedRef.current})`
            : 'Check timed out — results may appear later',
        })
        setTimeout(() => {
          setProgress({ state: 'idle', collected: 0, total: 0, message: '' })
        }, DONE_DISPLAY_MS)
      }
    }
  }, [siteId, queryClient, stopPolling])

  const startCheck = useCallback(async () => {
    // Reset state
    pollCountRef.current = 0
    totalCollectedRef.current = 0
    stopPolling()

    setProgress({ state: 'posted', collected: 0, total: 0, message: 'Submitting rank checks...' })

    try {
      const result = await apiClient.post<{ tasksPosted: number }>(
        `/api/sites/${siteId}/keywords/check`
      )
      totalPostedRef.current = result.tasksPosted

      if (result.tasksPosted === 0) {
        setProgress({ state: 'done', collected: 0, total: 0, message: 'No keywords to check' })
        setTimeout(() => {
          setProgress({ state: 'idle', collected: 0, total: 0, message: '' })
        }, DONE_DISPLAY_MS)
        return
      }

      // Start polling after initial delay
      setProgress({
        state: 'collecting',
        collected: 0,
        total: result.tasksPosted,
        message: `Waiting for results... (0 of ${result.tasksPosted})`,
      })

      // Wait for initial delay, then start polling
      delayRef.current = setTimeout(() => {
        delayRef.current = null
        collectResults() // First collect attempt
        intervalRef.current = setInterval(collectResults, POLL_INTERVAL)
      }, INITIAL_DELAY)

    } catch {
      setProgress({ state: 'done', collected: 0, total: 0, message: 'Failed to submit rank checks' })
      setTimeout(() => {
        setProgress({ state: 'idle', collected: 0, total: 0, message: '' })
      }, DONE_DISPLAY_MS)
    }
  }, [siteId, collectResults, stopPolling])

  return { startCheck, progress }
}

/** Enrich keywords with search volume / CPC / competition */
export function useEnrichKeywords(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{ enriched: number; total: number }>(
      `/api/sites/${siteId}/keywords/enrich`
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['keywords', siteId] })
    },
  })
}
